import { useEffect, useState, useCallback } from "react";
import { trackEvent } from "@/lib/audit";
import { supabase } from "@/integrations/supabase/client";

/**
 * Goal Clean Mode store. When triggered, the app temporarily hides
 * non-essential overlays (chat, banners, prediction cards, floating CTAs)
 * so the pitch can be seen clearly during goals, penalties, VAR, or
 * shots on target. Restores automatically after `durationMs`.
 */
const CLEAN_MODE_EVENT = "vs:goal_clean_mode";

let cleanModeUntil = 0;

export type CleanModeReason =
  | "goal"
  | "penalty"
  | "var"
  | "var_review"
  | "red_card"
  | "shot_on_target";

export function activateGoalCleanMode(reason: CleanModeReason, durationMs = 8000) {
  if (typeof window === "undefined") return;
  cleanModeUntil = Date.now() + durationMs;
  void trackEvent("goal_clean_mode_activated", { reason, durationMs });
  window.dispatchEvent(new CustomEvent(CLEAN_MODE_EVENT, { detail: { until: cleanModeUntil } }));
  window.setTimeout(() => {
    if (Date.now() >= cleanModeUntil) {
      window.dispatchEvent(new CustomEvent(CLEAN_MODE_EVENT, { detail: { until: 0 } }));
    }
  }, durationMs + 50);
}

export function useCleanMode(): boolean {
  const [active, setActive] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ until: number }>).detail;
      setActive(detail.until > Date.now());
    };
    window.addEventListener(CLEAN_MODE_EVENT, handler);
    return () => window.removeEventListener(CLEAN_MODE_EVENT, handler);
  }, []);
  return active;
}

/**
 * Detects if the on-screen keyboard is visible on mobile by comparing
 * visualViewport.height against window.innerHeight.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => {
      const heightDiff = window.innerHeight - vv.height;
      setOpen(heightDiff > 150);
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);
  return open;
}

/**
 * Favorite match persistence (localStorage). Returns current favorite
 * match id and a toggle.
 */
const FAV_KEY = "vs:favorite_match_id";

export function useFavoriteMatch(matchId?: string) {
  const [favoriteId, setFavoriteId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(FAV_KEY);
  });

  // Sync from Supabase if authenticated.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id;
      if (!uid) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: rows } = await (supabase as any)
        .from("user_favorite_matches").select("match_id").eq("user_id", uid).limit(1);
      if (cancelled) return;
      const remote = rows?.[0]?.match_id as string | undefined;
      if (remote) {
        window.localStorage.setItem(FAV_KEY, remote);
        setFavoriteId(remote);
        void trackEvent("favorite_match_synced", { source: "remote", matchId: remote });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isFavorite = !!matchId && favoriteId === matchId;
  const toggle = useCallback(() => {
    if (!matchId) return;
    const next = isFavorite ? null : matchId;
    if (next) window.localStorage.setItem(FAV_KEY, next);
    else window.localStorage.removeItem(FAV_KEY);
    setFavoriteId(next);
    void trackEvent("favorite_match_selected", { matchId, action: next ? "add" : "remove" });
    // Best-effort Supabase sync (no-op when guest)
    void (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id;
      if (!uid) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      if (next) {
        await sb.from("user_favorite_matches").delete().eq("user_id", uid);
        await sb.from("user_favorite_matches").insert({ user_id: uid, match_id: next });
      } else {
        await sb.from("user_favorite_matches").delete().eq("user_id", uid).eq("match_id", matchId);
      }
      void trackEvent("favorite_match_synced", { source: "local", matchId, action: next ? "add" : "remove" });
    })();
  }, [matchId, isFavorite]);
  return { favoriteId, isFavorite, toggle };
}